import { Check, ImageOff, MessageSquareText, Undo2 } from "lucide-react";
import { useState } from "react";

import { medidaDoItem, qtdDoItem } from "../../../lib/unidadeVenda";
import { agruparComponentes, duracaoAtePronto, getImageUrl, horaCurta } from "../lib/kds";

function ItemPhoto({ url, name, apagado }) {
  const [error, setError] = useState(false);
  const src = getImageUrl(url);
  const base = `w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex-shrink-0 ${apagado ? "opacity-60" : ""}`;
  if (!src || error) {
    return (
      <div className={`${base} bg-black/10 flex items-center justify-center`}>
        <ImageOff className="w-4 h-4 text-black/30" />
      </div>
    );
  }
  return <img src={src} alt={name} onError={() => setError(true)} className={`${base} object-cover`} />;
}

/** Observação do item ou do pedido. Azul de propósito: é a única cor que não
 *  disputa com o semáforo do cartão (branco/amarelo/vermelho) e continua
 *  legível em cima de qualquer um dos três. */
export function Observacao({ texto, rotulo }) {
  return (
    <div className="mt-1.5 flex items-start gap-2 rounded-lg bg-blue-600 text-white px-2.5 py-1.5 shadow-sm">
      <MessageSquareText className="w-4 h-4 mt-0.5 shrink-0" />
      <p className="text-sm font-bold leading-snug break-words min-w-0">
        {rotulo && <span className="opacity-80 font-semibold mr-1">{rotulo}</span>}{texto}
      </p>
    </div>
  );
}

/** Uma linha de item dentro do cartão.
 *
 *  Estados: normal, SELECIONADO (tocado, esperando o botão da comanda
 *  confirmar) e PRONTO (opaco, com selo — continua na comanda de origem de
 *  propósito, pra cozinha enxergar o pedido inteiro).
 *
 *  Tocar na linha só seleciona — nunca grava nada (regra do dono: o botão da
 *  comanda é a confirmação). O pronto se desfaz pelo botão "Desfazer" dele,
 *  não pelo toque, pelo mesmo motivo. Na fila, a bolinha à esquerda mostra
 *  que dá pra escolher; com a comanda selecionada pelo teclado, a bolinha vira
 *  o subnúmero do produto.
 */
export default function ItemLinha({
  item, nivel, subNumero, marcado, onTocar, onDesfazer, findProductPhoto, ocupado,
  naFila = true, desde = null, produtos = null,
}) {
  const nome = item.name || item.nome || "Item";
  const grupos = agruparComponentes(item);
  const cancelado = !!item._cancelado;
  const pronto = !!item.pronto_at && !cancelado;
  const entregue = !!item.entregue_at && !cancelado;
  const clicavel = !!onTocar && !pronto && !cancelado;
  // Apagar e riscar só valem na fila da cozinha, onde o item pronto contrasta
  // com o que ainda falta. Na coluna Prontos tudo já saiu — riscar lá fazia o
  // cartão inteiro parecer cancelado.
  const apagado = pronto && naFila;

  return (
    <div
      style={{ breakInside: "avoid" }}
      onClick={clicavel && !ocupado ? () => onTocar(item) : undefined}
      aria-pressed={clicavel ? !!marcado : undefined}
      className={`flex gap-3 pb-3 rounded-lg transition-all ${clicavel ? "cursor-pointer select-none" : ""} ${
        marcado ? "ring-2 ring-orange-500 bg-orange-500/10 px-1.5 -mx-1.5 pt-1" : ""
      } ${ocupado ? "animate-pulse" : ""}`}
    >
      {clicavel && (subNumero != null ? (
        <div className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center text-sm font-bold self-start ${
          marcado ? "bg-orange-500 text-white" : nivel.indice
        }`}>
          {subNumero}
        </div>
      ) : (
        <div data-e2e="kds-bolinha" className={`w-6 h-6 mt-0.5 shrink-0 rounded-full border-2 flex items-center justify-center self-start ${
          marcado ? "bg-orange-500 border-orange-500 text-white" : `${nivel.texto} border-current opacity-40`
        }`}>
          {marcado && <Check className="w-4 h-4" strokeWidth={3} />}
        </div>
      ))}
      <div className={`flex gap-3 flex-1 min-w-0 ${apagado ? "opacity-50" : ""} ${cancelado ? "opacity-70" : ""}`} data-e2e={cancelado ? "kds-item-cancelado" : undefined}>
        <ItemPhoto url={item.photo_url || item.foto_url || findProductPhoto(nome)} name={nome} apagado={apagado} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-bold text-base ${nivel.timerEscuro ? "text-yellow-200" : "text-orange-600"}`}>
              {/* Quilo/litro (30/09/2026): peso sai "1,250 kg", pelo cadastro que o cartão já tem */}
              {qtdDoItem(item.quantity || item.quantidade || 1, medidaDoItem(item, produtos))}
            </span>
            <span className={`font-semibold text-base ${cancelado ? "text-red-600 line-through decoration-2" : nivel.texto} ${apagado ? "line-through decoration-2 decoration-current/40" : ""}`}>
              {nome}
            </span>
            {cancelado && (
              <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600 text-white">
                Cancelado · não fazer
              </span>
            )}
            {(item.product_type === "pizza" || item.pizza) && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-orange-500/15 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded">🍕 Pizza</span>
            )}
            {item._semSetorAqui && (
            <span title="A categoria deste produto não tem setor: ele aparece em todas as telas. Configure em Cardápio → Produtos → Categorias."
              className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-dashed border-current ${nivel.suave}`}>
              sem setor
            </span>
          )}
          {item.combo_type && (
              <span className={`text-xs font-medium ${nivel.suave}`}>({item.combo_type === "combo" ? "Combo" : "Simples"})</span>
            )}
            {(entregue || (pronto && naFila)) && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600 text-white">
                {/* Pronto leva quanto tempo demorou (diferencia do resto da
                  comanda); entregue leva a hora em que chegou na mesa. */}
              <Check className="w-3 h-3" />{entregue
                ? `Entregue ${horaCurta(item.entregue_at)}`.trim()
                : item._auto_pronto
                  ? "Pronto · sem preparo"
                  : `Pronto ${duracaoAtePronto(desde, item.pronto_at)}`.trim()}
              </span>
            )}
          </div>
          {item.observacao && <Observacao texto={item.observacao} />}
          {grupos.length > 0 && (
            <div className="mt-1.5 space-y-1.5">
              {grupos.map((g, gi) => (
                <div key={`${g.etapa}-${gi}`}>
                  {g.etapa && <p className={`text-[10px] uppercase tracking-wider font-bold mb-0.5 ${nivel.rotulo}`}>{g.etapa}</p>}
                  {g.itens.map((c, ci) => {
                    const foto = c.foto || findProductPhoto(c.nome);
                    return (
                      <div key={ci} className={`flex items-center gap-2 text-sm font-medium ml-1 ${nivel.texto}`}>
                        {foto
                          ? <img src={getImageUrl(foto)} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                          : <span className={`w-7 text-center flex-shrink-0 ${nivel.suave}`}>•</span>}
                        <span className="min-w-0 break-words">{c.nome}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {apagado && onDesfazer && (
        <button type="button" disabled={ocupado} onClick={() => onDesfazer(item)}
          title="Desfazer: este produto ainda não saiu"
          className={`shrink-0 self-start inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md hover:bg-black/10 ${nivel.suave}`}>
          <Undo2 className="w-3.5 h-3.5" />Desfazer
        </button>
      )}
    </div>
  );
}
